/**
 * Clientes — Zona 2 (Desarrollo Comercial).
 *
 * IMPL-038 (2026-05-13) · Sprint 3 · rediseño en tabla escritorio.
 *   - 1 fila por cliente con 9 columnas (Nombre · Flota · Diagnóstico · Foto · IA · Oferta · Nº · Fecha · Importe).
 *   - 1b: muestra la oferta más reciente del cliente (independiente de reunión).
 *   - 2a: FichaCliente y OportunidadDetalle eliminados · navegación directa al stepper/constructor.
 *   - 3b: cliente sin diagnóstico → icono diagnóstico ámbar, IA/Oferta gris.
 *         Foto: gris si 0 respuestas, verde con ≥1 respuesta (la foto se genera con cualquier dato).
 *
 * Endpoints: /api/clientes, /api/reuniones, /api/ofertas (con paginación BACKLOG-007).
 */
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Search, Loader2, Users, Building2, X,
  Check, Camera, Sparkles, FileText, Pencil, MessageSquare, ClipboardList, CheckSquare,
  LayoutDashboard, TrendingUp, Briefcase, Inbox, Zap, ChevronRight,
} from "lucide-react";
import ReunionConsultiva from "./ReunionConsultiva";
import ConstructorOferta from "./ConstructorOferta";
import InformeConsultivo from "./InformeConsultivo";
import ActividadCliente from "./ActividadCliente";
import TareasGlobal from "./TareasGlobal";
import ZonaNav from "./ZonaNav";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authAxios = axios.create();
authAxios.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("mobilsafe_token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

const ESTADOS = [
  { v: "activo",     label: "Activo",     color: "bg-emerald-100 text-emerald-700" },
  { v: "pausado",    label: "Pausado",    color: "bg-amber-100 text-amber-700" },
  { v: "ganado",     label: "Ganado",     color: "bg-blue-100 text-blue-700" },
  { v: "perdido",    label: "Perdido",    color: "bg-rose-100 text-rose-700" },
  { v: "archivado",  label: "Archivado",  color: "bg-slate-100 text-slate-600" },
];

const EUR = (n) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

const TOTAL_EJES = 8;

export default function Clientes({ onBack }) {
  const [clientes, setClientes] = useState([]);
  const [reuniones, setReuniones] = useState([]);
  const [ofertas, setOfertas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("");
  const [showNuevo, setShowNuevo] = useState(false);
  const [editando, setEditando] = useState(null);             // cliente object o null
  const [reunionAbierta, setReunionAbierta] = useState(null); // {reunionId, pasoInicial, cliente}
  const [ofertaAbierta, setOfertaAbierta] = useState(null);   // {ofertaId, cliente}
  const [informeAbierto, setInformeAbierto] = useState(null); // reunion completa
  // Sprint B IMPL-046:
  const [actividadAbierta, setActividadAbierta] = useState(null); // cliente object o null
  const [vistaTareas, setVistaTareas] = useState(false);
  // IMPL-050:
  const [vistaActual, setVistaActual] = useState("dashboard"); // dashboard | lista | ofertas
  const [stats, setStats] = useState(null);
  const [borradorMio, setBorradorMio] = useState(null);
  // IMPL-051:
  const [ofertasIndependientes, setOfertasIndependientes] = useState([]);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const [rC, rR, rO, rS, rB, rOI] = await Promise.all([
        authAxios.get(`${API}/clientes?limit=500`),
        authAxios.get(`${API}/reuniones?limit=500`),
        authAxios.get(`${API}/ofertas?limit=500`),
        authAxios.get(`${API}/clientes/dashboard-stats`).catch(() => ({ data: null })),
        authAxios.get(`${API}/clientes/borrador-mio`).catch(() => ({ data: null })),
        authAxios.get(`${API}/ofertas/independientes`).catch(() => ({ data: [] })),
      ]);
      // IMPL-051 · ocultar borradores de la lista de clientes
      setClientes((rC.data || []).filter(c => !c.es_borrador));
      setReuniones(rR.data || []);
      setOfertas(rO.data || []);
      setStats(rS.data);
      setBorradorMio(rB.data);
      setOfertasIndependientes(rOI.data || []);
    } catch {
      toast.error("Error cargando datos");
    }
    setLoading(false);
  }

  // IMPL-038 · derivar pipeline por cliente
  const filas = useMemo(() => {
    const norm = (s) => (s || "").toLowerCase();
    return clientes
      .filter((c) => {
        if (filtroEstado && c.estado !== filtroEstado) return false;
        if (search && !`${c.empresa} ${c.cif || ""}`.toLowerCase().includes(norm(search))) return false;
        return true;
      })
      .map((c) => {
        // Reunión más reciente del cliente (para abrir cuestionario/foto/IA)
        const reusCli = reuniones
          .filter((r) => r.cliente_id === c.id)
          .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        const reu = reusCli[0] || null;

        // Oferta más reciente del cliente (independiente de reunión · 1b)
        const ofsCli = ofertas
          .filter((o) => o.cliente_id === c.id)
          .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        const oferta = ofsCli[0] || null;

        // Progreso del cuestionario
        const respuestasCount = (reu?.respuestas_cuestionario || []).length;
        const fa = reu?.flota_aproximada || {};
        const ejesContestados = new Set((reu?.respuestas_cuestionario || []).map((x) => x.eje));
        const tieneDatosCli  = !!(fa.datos_cliente && Object.values(fa.datos_cliente).some((v) => v && String(v).trim()));
        const tieneCompFlota = Array.isArray(fa.composicion) && fa.composicion.length > 0;
        const ejesCompletos =
          (tieneDatosCli ? 1 : 0) +
          (tieneCompFlota ? 1 : 0) +
          ["seguridad","combustible","operativa","mantenimiento","tic","sostenibilidad"]
            .filter((e) => ejesContestados.has(e)).length;

        const tieneDiagnostico = !!(reu?.diagnostico_ia?.resumen || reu?.diagnostico_ia?.texto_editado);
        // IMPL-038 · "tiene datos del cuestionario" = cualquier respuesta cualitativa, composición o ficha cliente
        const tieneAlgo = respuestasCount > 0 || tieneCompFlota || tieneDatosCli;

        // Estado por icono (verde · ámbar · gris). 3b modificado por usuario el 2026-05-13:
        //   Diagnóstico: ámbar siempre que no esté hecho.
        //   Foto: blocked si 0 respuestas · done si >=1 respuesta (la foto del antes se genera con cualquier dato).
        //   IA: blocked hasta que haya diagnóstico.
        //   Oferta: done si existe oferta · pending si hay diagnóstico sin oferta · blocked si nada.
        const stDiag   = tieneDiagnostico ? "done" : "pending";
        const stFoto   = !tieneAlgo ? "blocked" : "done";
        const stIA     = !tieneDiagnostico ? "blocked" : "done";
        const stOferta = oferta ? "done" : "pending";

        // Importe principal de la oferta (toma la 1ª versión · 1er escenario · balance del periodo si existe)
        const v0 = oferta?.versiones?.[(oferta.version_activa || 1) - 1] || oferta?.versiones?.[0];
        const e0 = v0?.escenarios?.[0];
        const importe = e0?.balance_periodo?.total_neto || oferta?.importe_actual || null;

        return {
          cliente: c, reunion: reu, oferta,
          ejesCompletos, respuestasCount,
          stDiag, stFoto, stIA, stOferta,
          fechaOferta: oferta?.created_at,
          numOferta: oferta?.id ? oferta.id.slice(0, 8).toUpperCase() : null,
          importe,
        };
      })
      .sort((a, b) => a.cliente.empresa.localeCompare(b.cliente.empresa, "es"));
  }, [clientes, reuniones, ofertas, search, filtroEstado]);

  // IMPL-050 · Separación borradores con contenido vs clientes
  // IMPL-051 · La lista de clientes ya viene filtrada (sin borradores).
  const filasClientesReales = useMemo(() => filas, [filas]);

  // IMPL-051 · Crear oferta sin cliente (va al borrador via /ofertas/borrador)
  async function crearOfertaIndependiente() {
    if (!borradorMio?.id) {
      toast.error("Contacta con administración para activar tu espacio de ofertas sin cliente");
      return;
    }
    try {
      const nombre = `Oferta sin cliente · ${new Date().toLocaleDateString("es-ES")}`;
      const r = await authAxios.post(`${API}/ofertas/borrador`, { nombre });
      setOfertasIndependientes((arr) => [r.data, ...arr]);
      setOfertaAbierta({ ofertaId: r.data.id, cliente: borradorMio });
    } catch {
      toast.error("Error creando oferta");
    }
  }
  async function cargarOfertaEnCliente(ofertaId, clienteDestinoId) {
    try {
      const r = await authAxios.patch(`${API}/ofertas/${ofertaId}/asignar-cliente`, { cliente_id: clienteDestinoId });
      const dest = clientes.find(c => c.id === clienteDestinoId);
      toast.success(`Oferta cargada en ${dest?.empresa || 'cliente'}`);
      await load();
      return r.data;
    } catch {
      toast.error("Error cargando oferta en cliente");
    }
  }

  // --- Vistas anidadas (stepper / constructor / informe) ---
  if (reunionAbierta) {
    return (
      <ReunionConsultiva
        reunionId={reunionAbierta.reunionId}
        cliente={reunionAbierta.cliente}
        pasoInicial={reunionAbierta.pasoInicial}
        onBack={() => { setReunionAbierta(null); load(); }}
        onAbrirInforme={(r) => { setReunionAbierta(null); setInformeAbierto(r); load(); }}
      />
    );
  }
  if (ofertaAbierta) {
    return (
      <ConstructorOferta
        ofertaId={ofertaAbierta.ofertaId}
        cliente={ofertaAbierta.cliente}
        onBack={() => { setOfertaAbierta(null); load(); }}
      />
    );
  }
  if (informeAbierto) {
    return (
      <InformeConsultivo
        reunion={informeAbierto}
        onBack={() => { setInformeAbierto(null); load(); }}
      />
    );
  }
  // Sprint B IMPL-046: Actividad del cliente
  if (actividadAbierta) {
    return (
      <ActividadCliente
        cliente={actividadAbierta}
        onBack={() => { setActividadAbierta(null); load(); }}
      />
    );
  }
  // Sprint B IMPL-046: Vista global de tareas
  if (vistaTareas) {
    return (
      <TareasGlobal
        onBack={() => setVistaTareas(false)}
        onOpenCliente={(c) => { setVistaTareas(false); setActividadAbierta(c); }}
      />
    );
  }

  // --- Navegación desde la tabla ---
  async function asegurarReunion(cliente) {
    // Devuelve la reunión más reciente del cliente o crea una si no hay
    const reusCli = reuniones
      .filter((r) => r.cliente_id === cliente.id)
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    if (reusCli[0]) return reusCli[0];
    try {
      const r = await authAxios.post(`${API}/reuniones`, {
        cliente_id: cliente.id,
        flota_aproximada: cliente?.tamano_flota_estimado
          ? { vehiculos: cliente.tamano_flota_estimado }
          : {},
      });
      toast.success("Reunión creada");
      setReuniones((arr) => [r.data, ...arr]);
      return r.data;
    } catch {
      toast.error("Error creando reunión");
      return null;
    }
  }

  async function irAPaso(fila, paso) {
    const reu = fila.reunion || await asegurarReunion(fila.cliente);
    if (!reu) return;
    setReunionAbierta({ reunionId: reu.id, pasoInicial: paso, cliente: fila.cliente });
  }

  async function clickFila(fila) {
    // Click en fila: enruta al paso correcto según estado (regla del spec).
    if (fila.oferta) {
      setOfertaAbierta({ ofertaId: fila.oferta.id, cliente: fila.cliente });
    } else if (fila.reunion && (fila.reunion.diagnostico_ia?.resumen || fila.reunion.diagnostico_ia?.texto_editado)) {
      setReunionAbierta({ reunionId: fila.reunion.id, pasoInicial: 4, cliente: fila.cliente });
    } else {
      await irAPaso(fila, 2);
    }
  }

  async function nuevaOfertaPara(fila) {
    try {
      const payload = {
        cliente_id: fila.cliente.id,
        nombre: `Oferta ${fila.cliente.empresa} · ${new Date().toLocaleDateString("es-ES")}`,
        reunion_id: fila.reunion?.id || null,
      };
      const r = await authAxios.post(`${API}/ofertas`, payload);
      setOfertas((arr) => [r.data, ...arr]);
      setOfertaAbierta({ ofertaId: r.data.id, cliente: fila.cliente });
    } catch {
      toast.error("Error creando oferta");
    }
  }

  // IMPL-072 · Vistas Dashboard y Ofertas independientes eliminadas (v3.3).
  // Toda la Zona 2 muestra directamente la lista de clientes con KPIs encima.

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={onBack} data-testid="clientes-back-btn" className="p-2 hover:bg-slate-100 rounded-lg">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <Users className="w-5 h-5" /> Clientes
              </h1>
              <p className="text-xs text-slate-500">Zona 2 · Desarrollo Comercial</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 py-6">
        {/* IMPL-072 · KPIs de la zona (4 tarjetas) encima de la lista */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="dc-kpis-zona">
          <KPIZonaCard testid="dc-kpi-clientes-activos" label="Clientes activos" value={stats?.clientes_activos ?? "—"} tint="emerald" loading={loading} />
          <KPIZonaCard testid="dc-kpi-tareas-hoy"        label="Tareas hoy"        value={stats?.tareas_pendientes_hoy ?? "—"} tint="amber" loading={loading} />
          <KPIZonaCard testid="dc-kpi-ofertas-abiertas"  label="Ofertas abiertas"  value={stats?.ofertas_sin_cerrar ?? "—"} tint="indigo" loading={loading} />
          <KPIZonaCard testid="dc-kpi-ofertas-sin-cli"   label="Ofertas sin cliente" value={ofertasIndependientes?.length ?? 0} tint="slate" loading={loading} />
        </div>

        {/* Toolbar: buscador izda · botón nuevo dcha (IMPL-038) */}
        <div className="bg-white rounded-xl border p-3 mb-4 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar por empresa o CIF…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="clientes-search-input"
              className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            data-testid="clientes-filtro-estado"
            className="px-3 py-2 border rounded-lg text-sm bg-white"
          >
            <option value="">Todos los estados</option>
            {ESTADOS.map((e) => <option key={e.v} value={e.v}>{e.label}</option>)}
          </select>
          <span className="text-sm text-slate-500" data-testid="clientes-contador">
            {filas.length} {filas.length === 1 ? "cliente" : "clientes"}
          </span>
          <button
            onClick={() => setVistaTareas(true)}
            data-testid="clientes-tareas-btn"
            className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 text-sm font-medium ml-auto"
          >
            <CheckSquare className="w-4 h-4" /> Mis tareas
          </button>
          <button
            onClick={() => setShowNuevo(true)}
            data-testid="clientes-nuevo-btn"
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium"
          >
            <Plus className="w-4 h-4" /> Nuevo cliente
          </button>
        </div>

        {loading ? (
          <div className="bg-white rounded-xl border p-12 text-center">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
          </div>
        ) : filas.length === 0 ? (
          <div className="bg-white rounded-xl border p-12 text-center text-slate-500">
            <Building2 className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            <p className="font-medium">No hay clientes que coincidan</p>
            <p className="text-sm mt-1">Crea uno nuevo o convierte un lead desde el panel de Admin.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="clientes-tabla">
                <thead>
                  <tr className="bg-slate-50 border-b text-[10px] uppercase tracking-wider text-slate-500">
                    <th className="text-left px-4 py-2.5 font-semibold">Nombre</th>
                    <th className="text-right px-2 py-2.5 font-semibold">Flota</th>
                    <th className="text-center px-2 py-2.5 font-semibold">Diagnóstico</th>
                    <th className="text-center px-2 py-2.5 font-semibold">Foto</th>
                    <th className="text-center px-2 py-2.5 font-semibold">IA</th>
                    <th className="text-center px-2 py-2.5 font-semibold">Oferta</th>
                    <th className="text-left px-2 py-2.5 font-semibold">Nº oferta</th>
                    <th className="text-left px-2 py-2.5 font-semibold">Fecha oferta</th>
                    <th className="text-right px-2 py-2.5 font-semibold">Importe</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filasClientesReales.map((fila) => (
                    <FilaCliente
                      key={fila.cliente.id}
                      fila={fila}
                      onClickFila={() => clickFila(fila)}
                      onClickDiag={() => irAPaso(fila, 2)}
                      onClickFoto={() => irAPaso(fila, 3)}
                      onClickIA={() => irAPaso(fila, 4)}
                      onClickOferta={() => fila.oferta
                        ? setOfertaAbierta({ ofertaId: fila.oferta.id, cliente: fila.cliente })
                        : nuevaOfertaPara(fila)}
                      onEditar={() => setEditando(fila.cliente)}
                      onActividad={() => setActividadAbierta(fila.cliente)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {showNuevo && (
        <ModalNuevoCliente
          onClose={() => setShowNuevo(false)}
          onCreated={async (c) => { setShowNuevo(false); await load(); }}
        />
      )}
      {editando && (
        <ModalNuevoCliente
          cliente={editando}
          onClose={() => setEditando(null)}
          onCreated={async () => { setEditando(null); await load(); }}
        />
      )}
    </div>
  );
}

// ============ Fila tabla (IMPL-038) ============

function FilaCliente({ fila, onClickFila, onClickDiag, onClickFoto, onClickIA, onClickOferta, onEditar, onActividad }) {
  const c = fila.cliente;
  return (
    <tr
      className="hover:bg-slate-50 cursor-pointer transition"
      data-testid={`cliente-row-${c.id}`}
      onClick={onClickFila}
    >
      <td className="px-4 py-2.5 align-middle">
        <div className="font-semibold text-slate-900 truncate max-w-[260px]">{c.empresa}</div>
        <div className="text-[11px] text-slate-500 truncate max-w-[260px]">
          {c.contacto_principal?.nombre || "—"}
          {c.sector ? ` · ${c.sector}` : ""}
        </div>
      </td>
      <td className="px-2 py-2.5 text-right font-mono text-xs text-slate-600">
        {c.tamano_flota_estimado ? `${c.tamano_flota_estimado} veh` : "—"}
      </td>
      <td className="px-2 py-2.5">
        <BarraDiagnostico
          completos={fila.ejesCompletos}
          total={TOTAL_EJES}
          onClick={(ev) => { ev.stopPropagation(); onClickDiag(); }}
          testid={`diag-${c.id}`}
        />
      </td>
      <td className="px-2 py-2.5 text-center">
        <IconoEstado
          estado={fila.stFoto} icon={Camera}
          onClick={(ev) => { ev.stopPropagation(); if (fila.stFoto !== "blocked") onClickFoto(); }}
          testid={`foto-${c.id}`}
          tooltipDone="Ver Foto del antes" tooltipPending="Iniciar Foto del antes"
        />
      </td>
      <td className="px-2 py-2.5 text-center">
        <IconoEstado
          estado={fila.stIA} icon={Sparkles}
          onClick={(ev) => { ev.stopPropagation(); if (fila.stIA !== "blocked") onClickIA(); }}
          testid={`ia-${c.id}`}
          tooltipDone="Ver Diagnóstico IA" tooltipPending="Generar Diagnóstico IA"
        />
      </td>
      <td className="px-2 py-2.5 text-center">
        <IconoEstado
          estado={fila.stOferta} icon={FileText}
          onClick={(ev) => { ev.stopPropagation(); onClickOferta(); }}
          testid={`oferta-${c.id}`}
          tooltipDone="Abrir Oferta" tooltipPending="Crear Oferta"
        />
      </td>
      <td className="px-2 py-2.5 font-mono text-[11px] text-slate-600">
        {fila.numOferta || "—"}
      </td>
      <td className="px-2 py-2.5 text-xs text-slate-600">
        {fila.fechaOferta ? new Date(fila.fechaOferta).toLocaleDateString("es-ES") : "—"}
      </td>
      <td className="px-2 py-2.5 text-right font-mono text-xs text-slate-900">
        {fila.importe ? EUR(fila.importe) : "—"}
      </td>
      <td className="px-2 py-2.5 text-center">
        <div className="flex items-center justify-center gap-1">
          <button
            onClick={(ev) => { ev.stopPropagation(); onActividad(); }}
            data-testid={`actividad-${c.id}`}
            title="Ver actividad y notas del cliente"
            className="p-1.5 text-slate-400 hover:text-emerald-700 hover:bg-emerald-50 rounded"
          >
            <ClipboardList className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={(ev) => { ev.stopPropagation(); onEditar(); }}
            data-testid={`editar-${c.id}`}
            title="Editar datos del cliente"
            className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
        </div>
      </td>
    </tr>
  );
}

// ============ IMPL-050 + IMPL-051 · Dashboard de entrada ============

// IMPL-072 · Tarjeta KPI para la nueva cabecera de la Zona 2 (encima de la lista)
function KPIZonaCard({ label, value, tint = "emerald", loading, testid }) {
  const tints = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
    amber:   "border-amber-200 bg-amber-50 text-amber-800",
    indigo:  "border-indigo-200 bg-indigo-50 text-indigo-800",
    slate:   "border-slate-200 bg-slate-50 text-slate-700",
  };
  return (
    <div className={`rounded-lg border-2 ${tints[tint]} px-4 py-3`} data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wider font-bold opacity-70">{label}</p>
      <p className="text-2xl font-black tabular-nums mt-0.5">{loading ? "…" : value}</p>
    </div>
  );
}

// IMPL-072 · DashboardEntrada y OfertasIndependientes conservados por si se restauran (dead code)
function DashboardEntrada({ onBack, stats, loading, onVerLista, onVerOfertas, onVerTareas, onNuevaOferta, borradorMio }) { // eslint-disable-line no-unused-vars
  const cards = [
    { key: "clientes_activos",       label: "Clientes activos",     icon: Users,        val: stats?.clientes_activos ?? "—",        tone: "emerald" },
    { key: "tareas_pendientes_hoy",  label: "Tareas pendientes hoy", icon: CheckSquare, val: stats?.tareas_pendientes_hoy ?? "—",   tone: "amber" },
    { key: "ofertas_sin_cerrar",     label: "Ofertas sin cerrar",   icon: Briefcase,    val: stats?.ofertas_sin_cerrar ?? "—",      tone: "blue",
      sub: stats?.importe_ofertas_abiertas != null ? `€${stats.importe_ofertas_abiertas.toLocaleString("es-ES")}` : null },
    { key: "ofertas_independientes", label: "Ofertas sin cliente",  icon: Inbox,        val: stats?.ofertas_independientes ?? "—",  tone: "orange",
      onClick: onVerOfertas },
  ];
  const toneClass = (t) => ({
    emerald: "text-emerald-600 bg-emerald-50",
    amber:   "text-amber-600   bg-amber-50",
    blue:    "text-blue-600    bg-blue-50",
    orange:  "text-orange-600  bg-orange-50",
  }[t] || "text-slate-600 bg-slate-50");

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dc-dashboard">
      {/* IMPL-068 · Header verde MOBILSAFE + nav de zonas coherente */}
      <header className="bg-[#0a6640] px-6 py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-sm font-bold text-white truncate">Desarrollo comercial</span>
          <span className="text-xs text-white/60 hidden md:inline">Clientes · Reuniones · Ofertas</span>
        </div>
        <ZonaNav activa="desarrollo" esAdmin={true} />
      </header>
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <LayoutDashboard className="w-5 h-5" /> Desarrollo Comercial
            </h1>
            <p className="text-xs text-slate-500">Resumen y accesos rápidos</p>
          </div>
          <button
            onClick={onVerTareas}
            data-testid="dc-mis-tareas-btn"
            className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 text-sm font-medium"
          >
            <CheckSquare className="w-4 h-4" /> Mis tareas
          </button>
        </div>
      </div>

      <main className="max-w-[1400px] mx-auto px-6 py-8">
        {/* 4 cards métricas */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {cards.map((card) => (
            <div
              key={card.key}
              onClick={card.onClick}
              className={`bg-white border border-slate-200 rounded-xl p-5 transition ${card.onClick ? "hover:shadow-md cursor-pointer hover:border-orange-300" : "hover:shadow-md"}`}
              data-testid={`dc-stat-${card.key}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${toneClass(card.tone)}`}>
                  <card.icon className="w-5 h-5" />
                </div>
                {card.sub && <span className="text-xs font-semibold text-slate-500">{card.sub}</span>}
              </div>
              <div className="text-4xl font-black text-slate-900">
                {loading ? <Loader2 className="w-7 h-7 animate-spin text-slate-300" /> : card.val}
              </div>
              <div className="text-xs text-slate-500 mt-1 font-medium">{card.label}</div>
            </div>
          ))}
        </div>

        {/* IMPL-051 · 1 acceso rápido "Oferta sin cliente" */}
        <div className="mb-8">
          <button
            onClick={onNuevaOferta}
            data-testid="dc-quick-oferta"
            className="w-full sm:max-w-md text-left p-5 bg-white border-2 border-[#ec561c] rounded-xl hover:bg-orange-50 transition group"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 text-[#ec561c] font-bold">
                <Zap className="w-5 h-5" /> Oferta sin cliente
              </div>
              <ChevronRight className="w-5 h-5 text-[#ec561c] opacity-0 group-hover:opacity-100 transition" />
            </div>
            <p className="text-xs text-slate-500">
              Crea una oferta para una presentación rápida sin tener cliente dado de alta. Quedará en tu historial de ofertas independientes.
            </p>
          </button>
        </div>

        {/* Botón principal */}
        <button
          onClick={onVerLista}
          data-testid="dc-ver-clientes-btn"
          className="w-full sm:w-auto flex items-center gap-2 px-5 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-semibold shadow-sm transition"
        >
          <Users className="w-4 h-4" /> Ver todos los clientes
          <ChevronRight className="w-4 h-4" />
        </button>
      </main>
    </div>
  );
}

// ============ IMPL-051 · Historial de ofertas sin cliente ============

function OfertasIndependientes({ ofertas, clientes, loading, onBack, onIrLista, onNueva, onAbrir, onCargarEnCliente }) {
  const [cargandoOf, setCargandoOf] = useState(null); // oferta a la que asignar cliente
  return (
    <div className="min-h-screen bg-slate-50" data-testid="vista-ofertas-indep">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={onBack} data-testid="ofind-back-btn" className="p-2 hover:bg-slate-100 rounded-lg">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <Inbox className="w-5 h-5" /> Ofertas sin cliente
              </h1>
              <p className="text-xs text-slate-500">Pruebas y presentaciones rápidas</p>
            </div>
          </div>
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
            <button onClick={onBack}    data-testid="dc-tab-dashboard" className="px-3 py-1.5 text-xs font-semibold rounded-md text-slate-600 hover:bg-white">Dashboard</button>
            <button onClick={onIrLista} data-testid="dc-tab-clientes"  className="px-3 py-1.5 text-xs font-semibold rounded-md text-slate-600 hover:bg-white">Clientes</button>
            <button data-testid="dc-tab-ofertas" className="px-3 py-1.5 text-xs font-semibold rounded-md bg-white text-emerald-700 shadow-sm">Ofertas sin cliente</button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 py-6">
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm text-slate-500" data-testid="ofind-contador">
            {ofertas.length} {ofertas.length === 1 ? "oferta" : "ofertas"}
          </span>
          <button
            onClick={onNueva}
            data-testid="ofind-nueva-btn"
            className="flex items-center gap-2 px-4 py-2 bg-[#ec561c] text-white rounded-lg hover:bg-orange-600 text-sm font-semibold"
          >
            <Plus className="w-4 h-4" /> Nueva oferta sin cliente
          </button>
        </div>

        {loading ? (
          <div className="bg-white rounded-xl border p-12 text-center">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
          </div>
        ) : ofertas.length === 0 ? (
          <div className="bg-white rounded-xl border p-12 text-center text-slate-500" data-testid="ofind-vacio">
            <Inbox className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            <p className="font-medium">Aún no tienes ofertas sin cliente</p>
            <p className="text-sm mt-1">Crea una para preparar números antes de tener el cliente dado de alta.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border overflow-hidden">
            <table className="w-full text-sm" data-testid="ofind-tabla">
              <thead>
                <tr className="bg-slate-50 border-b text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="text-left px-4 py-2.5 font-semibold">Nombre / referencia</th>
                  <th className="text-left px-2 py-2.5 font-semibold">Fecha</th>
                  <th className="text-right px-2 py-2.5 font-semibold">Importe</th>
                  <th className="w-72 px-2 py-2.5 font-semibold text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {ofertas.map((o) => {
                  const fecha = o.created_at ? new Date(o.created_at).toLocaleDateString("es-ES") : "—";
                  const idCorto = (o.id || "").slice(0, 8).toUpperCase();
                  const nombre = o.nombre || `Oferta #${idCorto} · ${fecha}`;
                  const importe = o.importe_actual || 0;
                  return (
                    <tr key={o.id} className="hover:bg-slate-50" data-testid={`ofind-row-${o.id}`}>
                      <td className="px-4 py-3 font-semibold text-slate-900">{nombre}</td>
                      <td className="px-2 py-3 text-slate-600 text-xs">{fecha}</td>
                      <td className="px-2 py-3 text-right font-mono text-xs">
                        {importe > 0 ? `${importe.toLocaleString("es-ES")} €` : "—"}
                      </td>
                      <td className="px-2 py-3 text-right">
                        <button
                          onClick={() => onAbrir(o)}
                          data-testid={`ofind-abrir-${o.id}`}
                          className="px-3 py-1.5 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded mr-1"
                        >
                          Abrir
                        </button>
                        <button
                          onClick={() => setCargandoOf(o)}
                          data-testid={`ofind-cargar-${o.id}`}
                          className="px-3 py-1.5 text-xs font-semibold text-orange-700 bg-orange-100 hover:bg-orange-200 rounded"
                        >
                          Cargar en cliente →
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {cargandoOf && (
        <ModalCargarEnCliente
          oferta={cargandoOf}
          clientes={clientes.filter(c => !c.es_borrador && c.estado !== "archivado")}
          onClose={() => setCargandoOf(null)}
          onConfirm={async (clienteId) => {
            await onCargarEnCliente(cargandoOf.id, clienteId);
            setCargandoOf(null);
          }}
        />
      )}
    </div>
  );
}

function ModalCargarEnCliente({ oferta, clientes, onClose, onConfirm }) {
  const [busqueda, setBusqueda] = useState("");
  const [seleccion, setSeleccion] = useState(null);
  const lista = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    const arr = clientes.filter(c => !q || (c.empresa || "").toLowerCase().includes(q));
    return arr.sort((a, b) => (a.empresa || "").localeCompare(b.empresa || "", "es"));
  }, [busqueda, clientes]);
  const idCorto = (oferta.id || "").slice(0, 8).toUpperCase();
  const nombre = oferta.nombre || `Oferta #${idCorto}`;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="modal-cargar-en-cliente">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <div>
            <h3 className="font-bold text-slate-900 flex items-center gap-2">
              <Inbox className="w-4 h-4 text-orange-600" /> Cargar oferta en cliente
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">Oferta: <span className="font-semibold">{nombre}</span></p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded" data-testid="cargar-close-btn">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-5 py-3 border-b">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar cliente…"
              data-testid="cargar-search"
              className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              autoFocus
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto" data-testid="cargar-lista">
          {lista.length === 0 ? (
            <p className="p-6 text-center text-sm text-slate-500">No hay clientes disponibles</p>
          ) : (
            lista.map((c) => (
              <button
                key={c.id}
                onClick={() => setSeleccion(c.id)}
                data-testid={`cargar-cliente-${c.id}`}
                className={`w-full px-5 py-3 text-left hover:bg-slate-50 border-b text-sm flex items-center justify-between ${seleccion === c.id ? "bg-emerald-50" : ""}`}
              >
                <div>
                  <div className="font-semibold text-slate-900">{c.empresa}</div>
                  <div className="text-xs text-slate-500">{c.contacto_principal?.nombre || ""}</div>
                </div>
                {seleccion === c.id && <Check className="w-4 h-4 text-emerald-600" />}
              </button>
            ))
          )}
        </div>
        <div className="px-5 py-3 border-t flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded">
            Cancelar
          </button>
          <button
            onClick={() => onConfirm(seleccion)}
            disabled={!seleccion}
            data-testid="cargar-confirmar-btn"
            className="px-4 py-2 text-sm font-semibold bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cargar en cliente
          </button>
        </div>
      </div>
    </div>
  );
}

// ============ IMPL-050 (deprecated en IMPL-051) · Modal asignar borrador ============
// Conservado por compatibilidad pero ya no se invoca. Eliminar en próxima limpieza.

function ModalAsignarBorrador({ borrador, clientes, onClose, onConfirm }) {
  const [busqueda, setBusqueda] = useState("");
  const [seleccion, setSeleccion] = useState(null);
  const lista = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    const arr = clientes.filter(c => !q || (c.empresa || "").toLowerCase().includes(q));
    return arr.sort((a, b) => (a.empresa || "").localeCompare(b.empresa || "", "es"));
  }, [busqueda, clientes]);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="modal-asignar-borrador">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <div>
            <h3 className="font-bold text-slate-900 flex items-center gap-2">
              <Inbox className="w-4 h-4 text-orange-600" /> Asignar todo a cliente real
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">Origen: <span className="font-semibold">{borrador.empresa}</span></p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded" data-testid="asignar-close-btn">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-5 py-3 border-b">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar cliente destino…"
              data-testid="asignar-search"
              className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              autoFocus
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto" data-testid="asignar-lista">
          {lista.length === 0 ? (
            <p className="p-6 text-center text-sm text-slate-500">No hay clientes disponibles</p>
          ) : (
            lista.map((c) => (
              <button
                key={c.id}
                onClick={() => setSeleccion(c.id)}
                data-testid={`asignar-cliente-${c.id}`}
                className={`w-full px-5 py-3 text-left hover:bg-slate-50 border-b text-sm flex items-center justify-between ${seleccion === c.id ? "bg-emerald-50" : ""}`}
              >
                <div>
                  <div className="font-semibold text-slate-900">{c.empresa}</div>
                  <div className="text-xs text-slate-500">{c.contacto_principal?.nombre || ""}</div>
                </div>
                {seleccion === c.id && <Check className="w-4 h-4 text-emerald-600" />}
              </button>
            ))
          )}
        </div>
        <div className="px-5 py-3 border-t flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded">
            Cancelar
          </button>
          <button
            onClick={() => onConfirm(seleccion)}
            disabled={!seleccion}
            data-testid="asignar-confirmar-btn"
            className="px-4 py-2 text-sm font-semibold bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Confirmar asignación
          </button>
        </div>
      </div>
    </div>
  );
}

function IconoEstado({ estado, icon: Icon, onClick, testid, tooltipDone, tooltipPending }) {
  const isDone = estado === "done";
  const isPending = estado === "pending";
  const isBlocked = estado === "blocked";
  const cls = isDone
    ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200 cursor-pointer"
    : isPending
      ? "bg-amber-100 text-amber-700 hover:bg-amber-200 cursor-pointer"
      : "bg-slate-100 text-slate-300 cursor-not-allowed";
  return (
    <button
      type="button"
      disabled={isBlocked}
      onClick={onClick}
      data-testid={testid}
      data-estado={estado}
      title={isBlocked ? "Disponible más adelante" : isDone ? tooltipDone : tooltipPending}
      className={`w-8 h-8 rounded-lg inline-flex items-center justify-center transition ${cls}`}
    >
      {isDone && <Check className="w-4 h-4" />}
      {isPending && <Icon className="w-4 h-4" />}
      {isBlocked && <Icon className="w-4 h-4" />}
    </button>
  );
}

function BarraDiagnostico({ completos, total, onClick, testid }) {
  const pct = Math.round((completos / total) * 100);
  const isDone = completos >= total;
  const isPending = completos === 0;
  const color = isDone ? "bg-emerald-500" : completos > 0 ? "bg-amber-500" : "bg-slate-300";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      data-completos={completos}
      title={isPending ? "Iniciar cuestionario" : isDone ? "Cuestionario completo · revisar" : "Continuar cuestionario"}
      className="w-full max-w-[180px] mx-auto block group"
    >
      <div className="flex items-center justify-between text-[10px] mb-1">
        <span className={`font-bold ${isDone ? "text-emerald-700" : isPending ? "text-amber-700" : "text-slate-600"}`}>
          {completos}/{total} ejes
        </span>
        <span className="text-slate-400 group-hover:text-slate-700">{pct}%</span>
      </div>
      <div className="flex gap-0.5">
        {Array.from({ length: total }).map((_, i) => (
          <span key={i}
            className={`flex-1 h-1.5 rounded ${i < completos ? color : "bg-slate-200"}`} />
        ))}
      </div>
      {isPending && (
        <div className="mt-1 text-[10px] text-amber-700 font-semibold flex items-center justify-center gap-1">
          <MessageSquare className="w-2.5 h-2.5" /> Iniciar cuestionario
        </div>
      )}
    </button>
  );
}


// ============ Modal: nuevo cliente ============

function ModalNuevoCliente({ onClose, onCreated, cliente = null }) {
  const modoEdicion = !!cliente;
  const [empresa, setEmpresa] = useState(cliente?.empresa || "");
  const [nombre, setNombre] = useState(cliente?.contacto_principal?.nombre || "");
  const [telefono, setTelefono] = useState(cliente?.contacto_principal?.telefono || "");
  const [cif, setCif] = useState(cliente?.cif || "");
  const [sector, setSector] = useState(cliente?.sector || "");
  const [flota, setFlota] = useState(
    cliente?.tamano_flota_estimado ? String(cliente.tamano_flota_estimado) : ""
  );
  // IMPL-065 · logo del cliente
  const [web, setWeb] = useState(cliente?.web || "");
  const [logoUrl, setLogoUrl] = useState(cliente?.logo_url || "");
  const [logoOrigen, setLogoOrigen] = useState(cliente?.logo_origen || null);  // "manual" | "clearbit" | null
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [refetching, setRefetching] = useState(false);
  const [saving, setSaving] = useState(false);

  // Normaliza el dominio: quita http(s), www., paths...
  const dominioLimpio = (web || "").trim().toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .split("/")[0];

  // URL a mostrar en el preview: prioridad logo_url subido > Clearbit por dominio
  const token = localStorage.getItem("mobilsafe_token") || "";
  const previewLogoUrl = logoUrl
    ? `${API}${logoUrl}?auth=${encodeURIComponent(token)}`
    : (dominioLimpio ? `https://icons.duckduckgo.com/ip3/${dominioLimpio}.ico` : "");

  async function subirLogo(file) {
    if (!file) return;
    if (!modoEdicion) {
      toast.error("Guarda el cliente primero, luego sube el logo");
      return;
    }
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await authAxios.post(`${API}/clientes/${cliente.id}/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLogoUrl(r.data.logo_url);
      setLogoOrigen("manual");
      toast.success("Logo actualizado");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Error subiendo logo";
      toast.error(msg);
    }
    setUploadingLogo(false);
  }

  async function quitarLogo() {
    if (!modoEdicion) { setLogoUrl(""); setLogoOrigen(null); return; }
    try {
      const r = await authAxios.delete(`${API}/clientes/${cliente.id}/logo`);
      if (r.data.replaced_with === "clearbit") {
        setLogoUrl(r.data.logo_url);
        setLogoOrigen("clearbit");
        toast.success("Logo manual eliminado · sustituido por Clearbit automático");
      } else {
        setLogoUrl("");
        setLogoOrigen(null);
        toast.success("Logo eliminado");
      }
    } catch {
      toast.error("Error eliminando logo");
    }
  }

  async function buscarLogoAuto() {
    if (!modoEdicion) {
      toast.error("Guarda el cliente primero");
      return;
    }
    if (!dominioLimpio) {
      toast.error("Introduce primero un dominio en 'Web'");
      return;
    }
    setRefetching(true);
    try {
      const r = await authAxios.post(`${API}/clientes/${cliente.id}/logo/refetch`);
      setLogoUrl(r.data.logo_url);
      setLogoOrigen("clearbit");
      toast.success("Logo encontrado y guardado");
    } catch (e) {
      const msg = e?.response?.data?.detail || "No se ha podido encontrar un logo automático";
      toast.error(msg);
    }
    setRefetching(false);
  }

  async function guardar() {
    if (!empresa.trim() || !nombre.trim()) {
      toast.error("Empresa y nombre de contacto son obligatorios");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        empresa: empresa.trim(),
        contacto_principal: {
          nombre: nombre.trim(),
          telefono: telefono.trim() || null,
          email: cliente?.contacto_principal?.email || null,
          cargo: cliente?.contacto_principal?.cargo || null,
        },
        cif: cif.trim() || null,
        sector: sector.trim() || null,
        tamano_flota_estimado: flota.trim() ? parseInt(flota, 10) || null : null,
        web: dominioLimpio || null,   // IMPL-065
      };
      if (modoEdicion) {
        const r = await authAxios.put(`${API}/clientes/${cliente.id}`, payload);
        toast.success("Cliente actualizado");
        onCreated(r.data);
      } else {
        const r = await authAxios.post(`${API}/clientes`, payload);
        toast.success("Cliente creado");
        onCreated(r.data);
      }
    } catch (e) {
      toast.error(modoEdicion ? "Error actualizando cliente" : "Error creando cliente");
    }
    setSaving(false);
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md" data-testid={modoEdicion ? "modal-editar-cliente" : "modal-nuevo-cliente"}>
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h2 className="font-bold text-slate-900">{modoEdicion ? "Editar cliente" : "Nuevo cliente"}</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
          <Field label="Empresa *" value={empresa} onChange={setEmpresa} testid="nc-empresa" />
          <Field label="Nombre contacto *" value={nombre} onChange={setNombre} testid="nc-nombre" />
          <Field label="Teléfono" value={telefono} onChange={setTelefono} testid="nc-telefono" />
          <Field label="CIF" value={cif} onChange={setCif} testid="nc-cif" />
          <Field label="Sector" value={sector} onChange={setSector} testid="nc-sector" />
          <Field label="Flota estimada (vehículos)" value={flota} onChange={setFlota} type="number" testid="nc-flota" />

          {/* IMPL-065 · logo del cliente */}
          <div className="pt-2 border-t">
            <Field label="Web (dominio, ej. iberdrola.com)" value={web} onChange={setWeb} testid="nc-web" />
            <div className="mt-3 flex items-center gap-3" data-testid="nc-logo-preview-row">
              <div className="w-16 h-16 rounded-lg border-2 border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
                {previewLogoUrl ? (
                  <img
                    src={previewLogoUrl}
                    alt="Logo cliente"
                    className="max-w-full max-h-full object-contain"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    data-testid="nc-logo-preview-img"
                  />
                ) : (
                  <span className="text-xl font-black text-slate-400">
                    {(empresa || "?").slice(0, 2).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="flex-1">
                <label className={`cursor-pointer inline-block px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-300 hover:bg-slate-50 ${uploadingLogo ? "opacity-50 pointer-events-none" : ""}`}>
                  {uploadingLogo ? "Subiendo..." : (logoUrl ? "Cambiar logo" : "Subir logo")}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                    className="hidden"
                    onChange={(e) => subirLogo(e.target.files?.[0])}
                    data-testid="nc-logo-upload-input"
                  />
                </label>
                {dominioLimpio && (
                  <button
                    type="button"
                    onClick={buscarLogoAuto}
                    disabled={refetching}
                    className="ml-2 text-xs px-2 py-1 rounded border border-slate-300 hover:bg-slate-50 disabled:opacity-50"
                    data-testid="nc-logo-auto-btn"
                    title="Descargar automáticamente vía Clearbit"
                  >
                    {refetching ? "Buscando..." : "🌐 Buscar auto"}
                  </button>
                )}
                {logoUrl && (
                  <button
                    type="button"
                    onClick={quitarLogo}
                    className="ml-2 text-xs text-rose-600 hover:underline"
                    data-testid="nc-logo-remove-btn"
                  >
                    Quitar
                  </button>
                )}
                <p className="text-[10px] text-slate-400 mt-1" data-testid="nc-logo-origen">
                  {logoOrigen === "manual" && "🔒 Logo subido manualmente"}
                  {logoOrigen === "clearbit" && "🌐 Logo automático (DuckDuckGo Icons)"}
                  {!logoOrigen && (dominioLimpio ? "Preview auto vía Clearbit (dominio)" : "PNG/JPG/WEBP · máx 2 MB")}
                </p>
              </div>
            </div>
          </div>

          {!modoEdicion && (
            <p className="text-xs text-slate-500 italic">
              Los demás datos se rellenan después desde la ficha del cliente. Para subir un logo, crea primero el cliente.
            </p>
          )}
        </div>
        <div className="px-6 py-4 border-t flex justify-end gap-2 bg-slate-50 rounded-b-xl">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-200 rounded-lg">Cancelar</button>
          <button
            onClick={guardar}
            disabled={saving}
            data-testid="nc-guardar-btn"
            className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} {modoEdicion ? "Guardar" : "Crear"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
      />
    </div>
  );
}
